import { z } from 'zod';
import { Id, TimestampMs } from './common.js';
import { ContentAckFrame, ContentEventBatchFrame, ContentSubscribeFrame } from './content.js';
import { DeviceAlertKind, DeviceAlertLevel } from './device-alerts.js';
import { DigestSlot } from './digests.js';
import { TranscriptEvent } from './events.js';
import { StorageStatusFrame } from './history.js';
import { LoadNumbers } from './load.js';
import { MachineResourcesStatus } from './machine-resources.js'; // machine-resources
import { TaskPlanTag } from './plan-batch.js'; // g18-stack
import { ReportStats } from './request-budget.js';
import { MachineResources } from './resources.js';
import { CommandRejectReason, DeviceEndorsement, SignedCommand, WebAuthnAssertion, } from './signing.js';
import { ProviderUsage } from './usage.js';
import { PROTOCOL_VERSION } from './version.js';
/** Daemon resource hygiene contracts (G6), exported through the wire module. */
export * from './resources.js';
/**
 * Wire protocol: vikod ⇄ UserHub ⇄ clients, JSON frames over WSS.
 *
 * Delivery rules (docs/protocol.md):
 *  - The daemon assigns per-run seq; it keeps events in its outbox until the hub acks.
 *  - The hub dedupes on (runId, seq) and acks the highest *contiguous* seq only after
 *    a durable write.
 *  - Clients keep their own per-run cursor and resume with `subscribe{fromSeq}`.
 *  - Every command is a SignedCommand; `envelope.id` is the idempotency key and the daemon
 *    executes each id at most once (a duplicate gets the original outcome).
 */
// ---------------------------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------------------------
const f = (type, shape) => z.object({ type: z.literal(type), ...shape });
export const Role = z.enum(['daemon', 'client']);
export const HelloFrame = f('hello', {
    v: z.literal(PROTOCOL_VERSION),
    role: Role,
    /** Daemon: machine id. Client: device id. */
    peerId: Id,
    softwareVersion: z.string(),
    capabilities: z.array(z.string()).optional(),
});
/** hub → daemon: `createDaemonAuthChallenge()` = { hubNonce (256-bit), issuedAt }. */
export const ChallengeFrame = f('challenge', {
    hubNonce: z.string().min(16),
    issuedAt: TimestampMs,
});
/** daemon → hub: `respondToDaemonAuthChallenge()` output (@viko/crypto DaemonAuthResponse). */
export const AuthFrame = f('auth', {
    statement: z.object({
        v: z.literal(1),
        type: z.literal('viko.daemon-auth'),
        machineId: Id,
        daemonKeyId: Id,
        hubNonce: z.string(),
        hubOrigin: z.string(),
        issuedAt: TimestampMs,
    }),
    /** base64url signature under `viko/daemon-auth/v1`. */
    signature: z.string(),
});
export const WelcomeFrame = f('welcome', {
    encryptedEvents: z.literal(1).optional(),
    v: z.literal(PROTOCOL_VERSION),
    sessionId: Id,
    serverTime: TimestampMs,
    /** Daemon only: highest durably stored seq per run, so the outbox can resend the rest. */
    acked: z.array(z.object({ runId: Id, seq: z.number().int().nonnegative() })).optional(),
});
/** daemon → hub (new events) and hub → client (fan-out / replay). Events are contiguous and ascending. */
export const EventBatchFrame = f('event.batch', {
    runId: Id,
    events: z.array(TranscriptEvent).min(1),
    /** hub → client: true while replaying history below the live edge. */
    replay: z.boolean().optional(),
});
/** hub → daemon: every event with seq ≤ `seq` for `runId` is durably stored. */
export const AckFrame = f('ack', {
    runId: Id,
    seq: z.number().int().nonnegative(),
});
/** client → hub: start streaming `runId` from `fromSeq` (exclusive: send events with seq > fromSeq). */
export const SubscribeFrame = f('subscribe', {
    runId: Id,
    fromSeq: z.number().int().nonnegative(),
    /** Include live-only kinds (asst.delta) when subscribing at the live edge. Default true. */
    live: z.boolean().optional(),
});
export const UnsubscribeFrame = f('unsubscribe', { runId: Id });
/** client → hub: persist the client's read cursor (for unread counts / cross-device resume). */
export const CursorFrame = f('cursor', {
    runId: Id,
    seq: z.number().int().nonnegative(),
});
/** hub → client: history below `toSeq` lives in cold storage chunks (R2); page with range reads. */
export const HistoryFrame = f('history', {
    runId: Id,
    chunks: z.array(z.object({
        key: z.string(),
        firstSeq: z.number().int().positive(),
        lastSeq: z.number().int().positive(),
        bytes: z.number().int().nonnegative(),
    })),
    /** Seq where the hot (DO SQLite) range begins. */
    hotFromSeq: z.number().int().positive(),
});
export const CommandFrame = f('command', { command: SignedCommand });
export const CommandStatus = z.enum([
    'accepted',
    'duplicate',
    'rejected',
    'delivered',
    'done',
    'failed',
]);
/** hub/daemon → client: lifecycle of a command, keyed by `envelope.id`. */
export const CommandResultFrame = f('command.result', {
    commandId: Id,
    status: CommandStatus,
    /** Set when status = rejected. */
    reason: CommandRejectReason.optional(),
    error: z.string().optional(),
    /** Result data (e.g. created runId). */
    result: z.unknown().optional(),
});
/** burst-guard: `t` is optional so the exact `{"type":"ping"}` keepalive is hub auto-answered. */
export const PingFrame = f('ping', { t: TimestampMs.optional() });
export const PongFrame = f('pong', { t: TimestampMs.optional() });
export const ErrorFrame = f('error', {
    code: z.enum([
        'unsupported_version',
        'auth_failed',
        'bad_frame',
        'seq_gap',
        'rate_limited',
        'not_found',
        'forbidden',
        'internal',
    ]),
    message: z.string(),
    runId: z.string().optional(),
    /** seq_gap: the next seq the hub expects. */
    expectedSeq: z.number().int().positive().optional(),
});
// ---------------------------------------------------------------------------------------------
// Additive (v0): daemon ⇄ hub control frames. Hubs/daemons that predate them ignore them.
// ---------------------------------------------------------------------------------------------
/**
 * daemon → hub request (MCP tool backends that live in the hub: tasks, handoff, history,
 * artifacts, cross-machine release dispatch). The hub answers with `rpc.result{id}`.
 * Methods: see `DAEMON_RPC_METHODS`.
 */
export const RpcFrame = f('rpc', {
    id: Id,
    method: z.string(),
    params: z.unknown().optional(),
});
export const RpcResultFrame = f('rpc.result', {
    id: Id,
    ok: z.boolean(),
    result: z.unknown().optional(),
    error: z.object({ code: z.string(), message: z.string() }).optional(),
});
/** Hub RPC methods a daemon may call (params/results are documented in docs/protocol.md). */
export const DAEMON_RPC_METHODS = [
    'task.get',
    'task.update',
    'task.create',
    'task.createBatch', // g18-batch
    'workstream.get',
    'handoff',
    'history.search',
    'artifact.register',
    'artifact.get',
    'release.dispatch',
    'history.import',
];
const SyncTask = z.object({
    id: Id,
    title: z.string(),
    status: z.string(),
    workstreamId: z.string().optional(),
    labels: z.array(z.string()).optional(),
    dependsOn: z.array(z.string()).optional(),
    acceptance: z.string().optional(),
    /** additive (g18-stack): the plan this task belongs to; its workstream lands into the plan branch. */
    plan: TaskPlanTag.optional(),
});
/**
 * What a daemon needs to know about a project to run, land and ship it on *this* machine
 * (a projection of `viko.project.yaml` + hub state). Cached by the daemon.
 */
export const ProjectSnapshot = z.object({
    id: Id,
    name: z.string(),
    /** Main clone on this machine. */
    repoPath: z.string().optional(),
    remote: z.string().optional(),
    defaultBranch: z.string().optional(),
    worktree: z
        .object({
        strategy: z.enum(['worktree', 'pool', 'single-lane']),
        root: z.string().optional(),
        poolSize: z.number().int().positive().optional(),
    })
        .optional(),
    landing: z
        .object({
        targetBranch: z.string().optional(),
        mergeMode: z.enum(['ff', 'merge', 'squash', 'pr']).optional(),
        /** Shell command lines, run in order in the worktree after every rebase. */
        test: z.array(z.string()).optional(),
        build: z.array(z.string()).optional(),
        // ---- landing-modes ----
        tiers: z
            .array(z.object({
            name: z.string().optional(),
            paths: z.array(z.string()),
            test: z.array(z.string()).optional(),
            build: z.array(z.string()).optional(),
            strictness: z.number().int().optional(),
        }))
            .optional(),
        integrationBranches: z
            .array(z.object({
            branch: z.string(),
            into: z.string().optional(),
            children: z.array(z.string()).optional(),
        }))
            .optional(),
        // ---- end landing-modes ----
    })
        .optional(),
    loopProfile: z.enum(['fast', 'balanced', 'careful']).optional(),
    autonomy: z
        .object({
        level: z.string().optional(),
        overrides: z.record(z.string(), z.enum(['auto', 'ask'])).optional(),
    })
        .optional(),
    /** `viko.project.yaml` release lanes, verbatim. */
    releaseLanes: z.array(z.record(z.string(), z.unknown())).optional(),
    /** `viko.project.yaml` secrets section, verbatim. */
    secrets: z.record(z.string(), z.unknown()).optional(),
    workstreams: z
        .array(z.object({
        id: Id,
        name: z.string(),
        branch: z.string().optional(),
        brief: z.string().optional(),
    }))
        .optional(),
    tasks: z.array(SyncTask).optional(),
    rules: z.array(z.object({ id: Id, text: z.string(), scope: z.string() })).optional(),
    /** Additive: history import for this project (explicit consent required; default off). */
    history: z.object({ import: z.boolean().optional(), adopt: z.boolean().optional() }).optional(),
});
/** hub → daemon: project snapshots for this machine (`full` = replace the whole cache). */
export const ProjectSyncFrame = f('project.sync', {
    projects: z.array(ProjectSnapshot),
    full: z.boolean().optional(),
});
export const LandingState = z.enum([
    'awaiting_approval',
    'queued',
    'running',
    'landed',
    'failed',
    // additive (auto-land): the user cancelled a queued/running landing
    'cancelled',
    // additive (landing-failure-detail): the daemon stopped mid-landing; catch-up retries it once
    'interrupted',
]);
/** additive (landing-failure-detail): structured, never-bare reason for a failed/interrupted landing. */
export const LandingFailure = z.object({
    phase: z.enum([
        'rebase',
        'recipe',
        'push',
        'merge',
        'verify',
        'deploy',
        'refused',
        'interrupted',
        'cancelled',
        'error',
    ]),
    /** Human phase ("recipe step 2 of 3", "rebase onto main", "push"). */
    phaseLabel: z.string(),
    /** One-line, specific explanation (never just "landing failed"). */
    message: z.string(),
    command: z.string().optional(),
    exitCode: z.number().int().optional(),
    durationMs: z.number().nonnegative().optional(),
    /** Last ~40 lines of the failing output. */
    outputTail: z.string().optional(),
    /** Full log on the machine that ran the landing (openable from the inspector). */
    logPath: z.string().optional(),
    /** The same step also fails on the target branch's tip. */
    baselineFailing: z.boolean().optional(),
    /** Conflicting files for rebase failures. */
    files: z.array(z.string()).optional(),
    /** additive (landing-flake): failing tests/files and first error lines parsed from the runner output. */
    failedTests: z.array(z.string()).optional(),
    failedFiles: z.array(z.string()).optional(),
    errorLines: z.array(z.string()).optional(),
    /** Suggested next action for the user or agent. */
    nextAction: z.string(),
});
/** additive (auto-land): one landing-recipe command in a landing's drill-in log. */
export const LandingRecipeRun = z.object({
    name: z.string(),
    command: z.string(),
    status: z.enum(['pending', 'running', 'pass', 'fail', 'reused']),
    attempt: z.number().int().positive().optional(),
    exitCode: z.number().int().optional(),
    durationMs: z.number().nonnegative().optional(),
    startedAt: TimestampMs.optional(),
    /** Output tail (capped), for failures and the drill-in. */
    outputTail: z.string().optional(),
    /** On failure: `fails` = the target's tip fails the same way (not caused by this branch). */
    baseline: z.enum(['fails', 'passes', 'unknown']).optional(),
    /** Failure tolerated by an explicit, passkey-approved "land anyway". */
    accepted: z.boolean().optional(),
    /** additive (landing-flake): parsed failures, and tests that only passed on the single flake re-run. */
    failedTests: z.array(z.string()).optional(),
    failedFiles: z.array(z.string()).optional(),
    errorLines: z.array(z.string()).optional(),
    flaky: z.array(z.string()).optional(),
    /** Waiting for another heavy recipe on this machine to finish. */
    waiting: z.boolean().optional(),
});
/** daemon → hub: landing pipeline progress (one per step transition) and terminal result. */
export const LandingUpdateFrame = f('landing.update', {
    landingId: Id,
    state: LandingState,
    projectId: z.string().optional(),
    workstreamId: z.string().optional(),
    runId: z.string().optional(),
    branch: z.string().optional(),
    summary: z.string().optional(),
    taskIds: z.array(z.string()).optional(),
    step: z.string().optional(),
    stepStatus: z.enum(['start', 'ok', 'fail', 'skip']).optional(),
    attempt: z.number().int().positive().optional(),
    detail: z.string().optional(),
    /** Terminal states: the pipeline's LandingResult (status, sha, steps, …). */
    result: z.unknown().optional(),
    ts: TimestampMs,
    // ---- additive (auto-land) ----
    /** Queued by land-on-finish (project autonomy `land-main: auto`), not a tap. */
    auto: z.boolean().optional(),
    /** Branch HEAD this landing is for (one landing per HEAD). */
    headSha: z.string().optional(),
    /** Landing-recipe log snapshot (per command: status, exit code, duration, output tail). */
    recipe: z.array(LandingRecipeRun).optional(),
    /** Terminal failure summary for chips/cards ("Landing blocked: …"). */
    reason: z.string().optional(),
    /** Failed only on steps that also fail on the target (offer "land anyway"). */
    baselineFailing: z.boolean().optional(),
    /** additive (landing-failure-detail): structured failure / interruption detail. */
    failure: LandingFailure.optional(),
    // ---- landing-modes (additive) ----
    /** Branch this landing targets (an integration branch for stacked workstreams). */
    targetBranch: z.string().optional(),
    /** Gate evidence: which path tier(s) ran for this diff. */
    gate: z
        .object({
        tiers: z.array(z.string()),
        strictness: z.number().int(),
        files: z.number().int().nonnegative(),
        commands: z.array(z.string()),
        /** additive (g18-stack): why this gate ran ("quick check: plan wave 2 of 6; …"). */
        reason: z.string().max(400).optional(),
    })
        .optional(),
    /** A rebase/merge conflict became a task; the landing resumes when it is resolved. */
    waitingOn: z
        .object({ kind: z.literal('conflict-task'), taskId: z.string(), title: z.string() })
        .optional(),
    // ---- end landing-modes ----
});
/** daemon → hub: release-lane build card updates (the daemon's BuildRecord). */
export const BuildUpdateFrame = f('build.update', {
    buildId: Id,
    projectId: z.string(),
    laneId: z.string(),
    status: z.string(),
    record: z.unknown(),
});
/** daemon → hub: outcome of a `brain.triage` / `brain.retro` command (same `requestId`). */
export const BrainResultFrame = f('brain.result', {
    requestId: Id,
    kind: z.enum(['triage', 'retro']),
    ok: z.boolean(),
    /** triage: `TriageOutcome` (result + card); retro: `{set, llm?, llmError?}`. */
    result: z.unknown().optional(),
    error: z.string().optional(),
    /** BrainExecMeta (attempts, mode, costUsd, durationMs, model). */
    meta: z.unknown().optional(),
});
/** daemon → hub: machine readiness + load (after welcome, on change, periodically). */
/** Public provider metadata only. Never auth output, credential paths, or provider identity. */
export const ProviderAccountMetadata = z.object({
    /** Machine-local profile ID; distinct from actor identity and upstream shared quota identity.
     * Shell-safe because the PWA displays an explicit local CLI login instruction. */
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/),
    provider: z.enum(['claude', 'codex']),
    label: z.string().min(1).max(120),
    mode: z.enum(['legacy', 'isolated']),
    enabled: z.boolean(),
    readiness: z.enum(['unknown', 'ready', 'login-required', 'unavailable']),
    checkedAt: TimestampMs.optional(),
    /** Key its usage windows are reported under (`ProviderUsage.accountId`). Aliases of one upstream
     * account on several machines share it, so their quota is never summed. Opaque; no email. */
    usageKey: z.string().min(1).max(160).optional(),
    /** Live runs bound to this account on the machine (router pacing). */
    activeRuns: z.number().int().nonnegative().max(1000).optional(),
    /** An official CLI login started from the app is waiting for the browser sign-in. */
    loginPending: z.boolean().optional(),
});
export const ProviderAccountsMetadata = z.array(ProviderAccountMetadata).max(128);
export const MachineStatusFrame = f('machine.status', {
    usageAccounts: z
        .object({
        claude: z.string().min(1).max(160).optional(),
        codex: z.string().min(1).max(160).optional(),
    })
        .optional(),
    usage: z.array(ProviderUsage).max(32).optional(),
    providerAccounts: ProviderAccountsMetadata.optional(),
    ts: TimestampMs,
    activeRuns: z.number().int().nonnegative(),
    maxRuns: z.number().int().positive(),
    /** G1: numeric workstation load (scheduling metadata). */
    load: LoadNumbers.optional(),
    doctor: z
        .object({
        at: z.string(),
        summary: z.record(z.string(), z.number()),
        capabilities: z.array(z.string()),
        fixCards: z.array(z.unknown()),
    })
        .optional(),
    /** Open "Blocked: environment" records. */
    blockers: z
        .array(z.object({
        id: Id,
        runId: z.string(),
        cause: z.string(),
        title: z.string(),
        remediation: z.string(),
        autoFix: z.boolean(),
        detectedAt: TimestampMs,
        // ---- inbox-detail: what the Inbox shows (sealed in encrypted machine reports) ----
        /** Exact command that fixes it, when there is one. */
        command: z.string().max(2000).optional(),
        /** The evidence line that matched (scrubbed). */
        evidence: z.string().max(2000).optional(),
        /** Doctor check that would have caught this (`doctor.fix` target when auto-fixable). */
        doctorCheck: z.string().max(200).optional(),
        /** Last output lines before the stall (scrubbed). */
        logTail: z.array(z.string().max(500)).max(20).optional(),
        // ---- end inbox-detail ----
    }))
        .optional(),
    /** Pending release-lane approvals (tap / passkey to ship). */
    pendingShips: z
        .array(z.object({
        projectId: z.string(),
        laneId: z.string(),
        gate: z.string(),
        landings: z.number().int().nonnegative(),
    }))
        .optional(),
    /** Daemon resource hygiene (G6): counts, limits and alert codes. */
    resources: MachineResources.optional(),
    // ---- g2g3-alerts: active device/usage alerts (routed as opaque refs; detail is encrypted) ----
    deviceAlerts: z
        .array(z.object({
        id: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
        kind: DeviceAlertKind,
        level: DeviceAlertLevel,
        notify: z.boolean(),
    }))
        .max(16)
        .optional(),
    // ---- end g2g3-alerts ----
    // ---- g13-digests: digest-ready markers (sealed; the hub only sees random refs + slot codes) ----
    digestMarkers: z
        .array(z.object({
        id: z.string().min(1).max(200),
        slot: DigestSlot,
        notify: z.boolean(),
    }))
        .max(16)
        .optional(),
    // ---- end g13-digests ----
    // ---- machine-resources: the broker's holders/queue and disk floor (sealed report only) ----
    machineResources: MachineResourcesStatus.optional(),
    // ---- end machine-resources ----
    // ---- burst-guard: encrypted-report delivery counters (sealed report only) ----
    reportStats: ReportStats.optional(),
    // ---- end burst-guard ----
});
// ---------------------------------------------------------------------------------------------
// Additive (v0): control-plane frames (first defined in apps/control, moved here verbatim).
// ---------------------------------------------------------------------------------------------
export const InboxItem = z.object({
    /** questionId / approvalId. Unique only together with runId. */
    id: z.string(),
    runId: z.string(),
    /** Additive: `land` (landing awaiting a tap), `build` (ship tap), `card` (decision card). */
    type: z.enum(['question', 'approval', 'land', 'build', 'card']),
    seq: z.number().int(),
    title: z.string(),
    blocking: z.boolean(),
    createdAt: TimestampMs,
    score: z.number(),
});
export const RunSummary = z.object({
    runId: z.string(),
    machineId: z.string(),
    agent: z.string().optional(),
    model: z.string().optional(),
    status: z.string(),
    headSeq: z.number().int(),
});
/** hub → client: open questions/approvals, ranked (full snapshot on every change). */
export const InboxFrame = f('inbox', { items: z.array(InboxItem) });
/** hub → client: run metadata changed (status, model, head seq). */
export const RunFrame = f('run', { run: RunSummary });
/** `@viko/crypto` RevocationList (passkey-signed). */
export const RevocationList = z.object({
    v: z.literal(1),
    type: z.literal('viko.revocations'),
    seq: z.number().int().nonnegative(),
    issuedAt: TimestampMs,
    devices: z.array(z.string()),
    credentials: z.array(z.string()),
});
/**
 * hub → daemon, after `welcome` and on every change: endorsements and the latest signed
 * revocation list for the daemon's TrustStore. The daemon verifies each item itself
 * (docs/signing.md §4, §8); the hub only relays.
 */
export const TrustSyncFrame = f('trust.sync', {
    endorsements: z.array(DeviceEndorsement),
    revocations: z.object({ list: RevocationList, assertion: WebAuthnAssertion }).nullable(),
});
/** daemon → hub: request a presigned R2 upload URL on the artifact origin. */
export const ArtifactUploadFrame = f('artifact.upload', {
    reqId: z.string(),
    artifactId: z.string(),
    runId: z.string().optional(),
    name: z.string(),
    mime: z.string().optional(),
    bytes: z.number().int().nonnegative().optional(),
});
/** hub → daemon: presigned PUT URL (15 min). */
export const ArtifactUrlFrame = f('artifact.url', {
    reqId: z.string(),
    artifactId: z.string(),
    key: z.string(),
    url: z.string(),
    expiresAt: TimestampMs,
    /** Additive: artifact storage (R2) is not configured; keep the file queued (`storage.status`). */
    unavailable: z.boolean().optional(),
});
/** daemon → hub: per-repo landing lock (docs/PLAN.md landing pipeline). */
export const LockAcquireFrame = f('lock.acquire', {
    reqId: z.string(),
    repo: z.string(),
    ttlMs: z.number().int().positive().optional(),
});
export const LockReleaseFrame = f('lock.release', {
    reqId: z.string(),
    repo: z.string(),
    token: z.string(),
});
export const LockResultFrame = f('lock.result', {
    reqId: z.string(),
    ok: z.boolean(),
    token: z.string().optional(),
    expiresAt: TimestampMs.optional(),
    holder: z.string().optional(),
});
export const Frame = z.discriminatedUnion('type', [
    ContentAckFrame,
    ContentEventBatchFrame,
    ContentSubscribeFrame,
    HelloFrame,
    ChallengeFrame,
    AuthFrame,
    WelcomeFrame,
    EventBatchFrame,
    AckFrame,
    SubscribeFrame,
    UnsubscribeFrame,
    CursorFrame,
    HistoryFrame,
    CommandFrame,
    CommandResultFrame,
    PingFrame,
    PongFrame,
    ErrorFrame,
    RpcFrame,
    RpcResultFrame,
    ProjectSyncFrame,
    LandingUpdateFrame,
    BuildUpdateFrame,
    MachineStatusFrame,
    BrainResultFrame,
    InboxFrame,
    RunFrame,
    TrustSyncFrame,
    ArtifactUploadFrame,
    ArtifactUrlFrame,
    LockAcquireFrame,
    LockReleaseFrame,
    LockResultFrame,
    StorageStatusFrame,
]);
/** R2 cold-storage chunking: a chunk closes at whichever limit is hit first. */
export const R2_CHUNK_LIMITS = {
    maxEvents: 512,
    maxBytes: 1024 * 1024,
    maxAgeMs: 60_000,
};
//# sourceMappingURL=wire.js.map